import { trainStationCodeMapByWebsiteDomain } from "@/config/trainStationCodeMap"
import getSiteFromDomain from "@/vue/planning/logic/getSiteFromDomain"

/** Eurostar (London St Pancras) */
export const EUROSTAR_TRAIN_ORIGINS = ["STP"] as const

/** TNE trains */
export const TNE_TRAIN_ORIGINS = ["PAZ", "AMS", "RTM", "ANR", "BRU"] as const

export type EurostarTrainOrigin = (typeof EUROSTAR_TRAIN_ORIGINS)[number]
export type TneTrainOrigin = (typeof TNE_TRAIN_ORIGINS)[number]

/**
 * PFPTT + PFPTTUK and PFTT + PFTTUK are the same package type in the UI.
 * Eurostar codes (no UK suffix) are the canonical display codes.
 * HTT is Eurostar-only.
 */
export const TRAIN_PACKAGE_FAMILIES: Record<string, string[]> = {
	PFPTT: ["PFPTT", "PFPTTUK"],
	PFPTTUK: ["PFPTT", "PFPTTUK"],
	PFTT: ["PFTT", "PFTTUK"],
	PFTTUK: ["PFTT", "PFTTUK"],
	HTT: ["HTT"],
}

const EUROSTAR_PACK_BY_FAMILY: Record<string, string> = {
	PFPTT: "PFPTT",
	PFPTTUK: "PFPTT",
	PFTT: "PFTT",
	PFTTUK: "PFTT",
	HTT: "HTT",
}

const TNE_PACK_BY_FAMILY: Record<string, string> = {
	PFPTT: "PFPTTUK",
	PFPTTUK: "PFPTTUK",
	PFTT: "PFTTUK",
	PFTTUK: "PFTTUK",
}

export const isEurostarOrigin = (origin: string): boolean =>
	(EUROSTAR_TRAIN_ORIGINS as readonly string[]).includes(origin)

export const isTneOrigin = (origin: string): boolean =>
	(TNE_TRAIN_ORIGINS as readonly string[]).includes(origin)

export const getTrainPackageFamily = (packCode: string): string[] =>
	TRAIN_PACKAGE_FAMILIES[packCode] ?? [packCode]

export const getCanonicalTrainPackCode = (packCode: string): string =>
	EUROSTAR_PACK_BY_FAMILY[packCode] ?? packCode

export const isSameTrainPackageType = (packCodeA: string, packCodeB: string): boolean => {
	const family = TRAIN_PACKAGE_FAMILIES[packCodeA]
	if (!family) return packCodeA === packCodeB
	return family.includes(packCodeB)
}

/** Origins allowed for a train pack type. `null` means no extra restriction. */
export const getAllowedOriginsForTrainPack = (packCode: string): string[] | null => {
	if (packCode === "HTT") {
		return [...EUROSTAR_TRAIN_ORIGINS]
	}
	if (TRAIN_PACKAGE_FAMILIES[packCode]?.length === 2) {
		return [...EUROSTAR_TRAIN_ORIGINS, ...TNE_TRAIN_ORIGINS]
	}
	return null
}

/** Origins that belong to this pack code itself (Eurostar vs TNE), not the whole family. */
export const getNativeOriginsForTrainPack = (packCode: string): string[] | null => {
	if (!TRAIN_PACKAGE_FAMILIES[packCode]) return null
	if (packCode === "HTT" || packCode === EUROSTAR_PACK_BY_FAMILY[packCode]) {
		return [...EUROSTAR_TRAIN_ORIGINS]
	}
	if (packCode === TNE_PACK_BY_FAMILY[packCode]) {
		return [...TNE_TRAIN_ORIGINS]
	}
	return null
}

export const resolveTrainPackCodeForOrigin = (
	packCode: string,
	origin?: string | null
): string => {
	const family = TRAIN_PACKAGE_FAMILIES[packCode]
	if (!family) return packCode
	if (family.length === 1) return family[0]

	const originCode = origin?.split("-")[0] || ""
	if (isTneOrigin(originCode)) {
		return TNE_PACK_BY_FAMILY[packCode] ?? packCode
	}
	if (isEurostarOrigin(originCode)) {
		return EUROSTAR_PACK_BY_FAMILY[packCode] ?? packCode
	}
	return getCanonicalTrainPackCode(packCode)
}

export const variantBelongsToPackCode = (
	variantCode: string | undefined,
	packCode: string
): boolean => {
	if (!variantCode) return false
	return variantCode === packCode || variantCode.startsWith(`${packCode}_`)
}

export const dedupeTrainPackagesForDisplay = <T extends { codePackage: string }>(
	packs: T[]
): T[] => {
	const seenFamilies = new Set<string>()
	const result: T[] = []

	for (const pack of packs) {
		const family = TRAIN_PACKAGE_FAMILIES[pack.codePackage]
		if (!family) {
			result.push(pack)
			continue
		}
		const familyKey = family[0]
		if (seenFamilies.has(familyKey)) continue

		const canonicalPack = packs.find((p) => p.codePackage === familyKey)
		result.push(canonicalPack ?? pack)
		seenFamilies.add(familyKey)
	}

	return result
}

export const getPreferredTrainOrigin = (
	packCode: string,
	availableOrigins: string[] = []
): string | undefined => {
	const allowed = getAllowedOriginsForTrainPack(packCode)
	const country = getSiteFromDomain(window.location.hostname)
	const domainStations = Array.from(
		(trainStationCodeMapByWebsiteDomain[country] ?? new Map()).keys()
	)

	const pool = (availableOrigins.length ? availableOrigins : allowed ?? domainStations)
		.filter((origin) => !allowed || allowed.includes(origin))

	return domainStations.find((station) => pool.includes(station)) ?? pool[0] ?? allowed?.[0]
}

export const resolveTrainOriginForPack = (
	packCode: string,
	currentOrigin: string | null | undefined,
	availableOrigins: string[] = []
): string | undefined => {
	const allowed = getAllowedOriginsForTrainPack(packCode)
	const originCode = currentOrigin?.split("-")[0] || ""

	// Keep an origin already chosen (URL / user) if it is valid for this pack type.
	// Do not require it to be in `availableOrigins` yet — that list can be incomplete on first load.
	if (originCode && (!allowed || allowed.includes(originCode))) {
		return originCode
	}

	const pool = (availableOrigins.length ? availableOrigins : allowed ?? [])
		.filter((origin) => !allowed || allowed.includes(origin))

	return pool[0] ?? getPreferredTrainOrigin(packCode, availableOrigins)
}

export const isUkWebsite = (): boolean =>
	getSiteFromDomain(window.location.hostname) === "www.uk.travelski.com"

export const prestationMatchesOrigin = (
	prestation: { origins?: string[] },
	origin?: string | null
): boolean => {
	const originCode = origin?.split("-")[0] || ""
	if (!originCode) return true
	if (!Array.isArray(prestation.origins) || prestation.origins.length === 0) {
		return true
	}
	return prestation.origins.includes(originCode)
}

export const findTrainPrestIdForOrigin = (
	items: Array<{ codePackage?: string; prestId?: string; origins?: string[] }>,
	packCode: string,
	origin?: string | null
): string | undefined => {
	const family = getTrainPackageFamily(packCode)
	const belongsToFamily = (codePackage?: string) =>
		!!codePackage && family.some((familyPack) => variantBelongsToPackCode(codePackage, familyPack))

	const originMatch = items.find((item) => {
		if (!item.prestId || !belongsToFamily(item.codePackage)) {
			return false
		}
		return prestationMatchesOrigin(item, origin)
	})
	if (originMatch?.prestId) return originMatch.prestId

	const resolvedPack = resolveTrainPackCodeForOrigin(packCode, origin)
	const packMatch = items.find((item) => item.prestId && variantBelongsToPackCode(item.codePackage, resolvedPack))
	return packMatch?.prestId
}
